// Common and Materail-UI Imports
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Autocomplete, Avatar, Badge, Box, Button, Chip, Divider, IconButton, InputAdornment, ListItem, ListItemAvatar, ListItemText, Modal, styled, TextField, Tooltip } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CryptoJS from 'crypto-js';
import useApi from '../../Utilities/useAPI';

// View Connections Imports
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import TextsmsRoundedIcon from '@mui/icons-material/TextsmsRounded';
import DeleteIcon from '@mui/icons-material/Delete';

// Share Profile Imports
import QrCodeProfile from './QrCodeProfile';
import { useUserDetailContext } from '../../UserDetailContext';
import { saveAs } from 'file-saver';
import { ReactComponent as Download } from "../../../Assets/img/icons/Download-share.svg"
// import facebook from '../../../Assets/img/icons/Facebook.svg'
import whatsapp from '../../../Assets/img/icons/whatsapp.svg'
// import insta from '../../../Assets/img/icons/instagram.svg'
import linkedin from '../../../Assets/img/icons/linkedin.svg'
// import signal from '../../../Assets/img/icons/signal.svg'
import telegram from '../../../Assets/img/icons/Telegram.svg'

// Invite Profile Imports
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { PhoneInputMenuProps } from '../../Onboarding/ActiveStep4';
import { useQuantityContext } from '../../QuantityContext';
import { getConnectedDays } from './ParentProfile';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ANDROID_APP_LINK } from '../../../config/constant';


const ModalBox = styled(Box)({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    boxShadow: '0px 12px 24px 0px #919EAB1F, 0px 0px 2px 0px #919EAB33',
    borderRadius: '8px',
    width: "80%",
    maxWidth: "80%",
    maxHeight: "80%",
    overflowY: "auto"
});

const ContactBox = styled(Box)({
    borderBottom: '1px solid #D0D5DD4D',
    padding: '10px 24px',
    fontSize: '14px'
})

const ListBox1 = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: '50%',
    maxWidth: '50%'
})

const ListBox2 = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: '35%',
    maxWidth: '35%',
})

const ListBox3 = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: '15%',
    maxWidth: '15%',
    color: 'rgb(0, 0, 0, 0.87)',
    fontFamily: 'Roboto, sans-serif'
})

const ActiveBadge = styled(Badge)(({ theme, isActive }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: isActive ? '#4CAF50' : '#EB4E3D', // green if active, red if not
        color: isActive ? '#4CAF50' : '#EB4E3D', // match the badge color
        border: '1px solid white',
        right: '30%',
        transform: 'scale (1.8) translate (50%, -50%)',
        '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            content: '""',
            animation: isActive ? 'ripple 1.2s infinite ease-in-out' : '',
            border: isActive ? '1px solid currentColor' : '',
        },
    },
    '@keyframes ripple': {
        '0%': {
            transform: 'scale(.8)',
            opacity: 1,
        },
        '100%': {
            transform: 'scale(2.4)',
            opacity: 0,
        },
    },
}));

const copyToClipboard = (item, handleSuccess, handleFailure) => {
    navigator.clipboard.writeText(item).then(() => {
        handleSuccess('Link Copied!');
    }).catch((err) => {
        handleFailure('Could not Copy Link.')
        console.error("Failed to copy: ", err);
    });
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailHelperText = "Press Enter to add an e-mail to the list";
const phoneHelperText = "Press Enter to add a phone number to the list";

const androidAppLink = ANDROID_APP_LINK;

export const ViewConnections = ({ open, onClose, connectionCount, connectionList, setConnectionList, limit = 20 }) => {
    const { callApiFunction } = useApi();

    const [openSearch, setOpenSearch] = useState(false);
    const [searchContact, setSearchContact] = useState('');
    const [filteredContacts, setFilteredContacts] = useState(connectionList || []);

  

    const handleSearchClick = () => {
        if (openSearch === true)
            setOpenSearch(false);
        else
            setOpenSearch(true);
    }

    const handleInputChange = (event) => {
        setSearchContact(event.target.value);
    };

    const renderConnectedSince = (value, unit) => {
        if (unit === "day" || "days") {
            if (value === 0) return "today";
            else if (value === 1) return "yesterday";
        }
        return `${value} ${unit} ago`;
    }

    useEffect(() => {
        if (searchContact && searchContact.trim()) {
            const results = connectionList.filter(contact =>
                contact.contactName.toLowerCase().includes(searchContact.trim().toLowerCase())
            );
            setFilteredContacts(results);
        } else {
            setFilteredContacts(connectionList);
        }
    }, [searchContact, connectionList]);

    // API Call for fetching connections
    const [hasMore, setHasMore] = useState(true);

    // Wrap fetchConnections in useCallback
    const fetchConnections = useCallback(async () => {
        try {
            const response = await callApiFunction('ConnectedContacts', { limit: limit, offset: connectionList.length });

            if (response?.statusCode === 200) {
                const newConnections = response.data.map(member => ({
                    id: member.id,
                    profilePic: member.profile_url,
                    contactRedId: member.contact_ref_id,
                    encryptedContactRefId: CryptoJS.AES.encrypt(member.contact_ref_id.toString(), 'UserDetailsSecretKey').toString(),
                    contactName: `${member.first_name} ${member.last_name}`,
                    contactRole: member.relationship_type,
                    isVerified: member.is_verified,
                    isFavourite: member?.is_favourite,
                    isActive: true,
                    connectedSince: getConnectedDays(member.created_at),
                    role: member.designation || member.field_of_study || member.title || '---',
                    subTitle: member.company || member.institute || member.nickname || '---',
                }));

                setConnectionList(prev => [...prev, ...newConnections]);

                if (newConnections.length < limit) {
                    setHasMore(false); // No more data if fewer than limit
                }
            }
        } catch (err) {
            console.error('Error fetching connections:', err);
        }
    }, [connectionList.length, setConnectionList]);

    // Load connections when the modal is opened for the first time
    useEffect(() => {
        if (open && connectionList.length === 0) {
            fetchConnections();
        }
    }, [open, connectionList.length, fetchConnections]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox>
                <div className="py-4 px-3 d-flex justify-content-between">
                    <div className="profile-meta-info d-flex align-items-center ms-4">
                        <GroupsIcon /> <span className="color-blue">{connectionCount || 0} Connections</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <div className={openSearch ? "me-3" : "me-2"}>
                            {openSearch === false ? (
                                <IconButton onClick={handleSearchClick}>
                                    <SearchIcon className="icon-color" />
                                </IconButton>
                            ) : (
                                <TextField
                                    placeholder="Search for People"
                                    variant="outlined"
                                    size="small"
                                    value={searchContact}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div className="icon-svg-24 flex-center ml-0">
                                                    <SearchIcon className="icon-color" />
                                                </div>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: '320px' }}
                                />
                            )}
                        </div>
                        <IconButton onClick={onClose} disableTouchRipple>
                            <CloseRoundedIcon />
                        </IconButton>
                    </div>
                </div>
                <Divider className='m-2' sx={{ backgroundColor: "#DDE0E6", height: "1px", opacity: 1 }} />
                <InfiniteScroll
                    dataLength={connectionList.length}
                    next={fetchConnections}
                    hasMore={hasMore}
                    loader={<p>Loading...</p>}
                >
                    <div className="px-3 pb-4">
                        {filteredContacts.map((connection) => {
                            const contactProfileUrl = `/profile?encryptedId=${encodeURIComponent(connection.encryptedContactRefId)}`;

                            return (
                                // Each individual connection goes here
                                <ContactBox className='connection-row' key={connection.id}>
                                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px', p: 0 }}>

                                        <ListBox1 component={connection.isVerified ? Link : undefined} to={contactProfileUrl} target='_blank' rel='noreferrer'>
                                            <ListItemAvatar>
                                                <ActiveBadge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    variant="dot"
                                                    isActive={connection.isVerified} // Pass the isActive prop here
                                                >
                                                    <Avatar src={connection.profilePic} alt={connection.contactName} sx={{ height: '50px', width: '50px', marginRight: '12px' }}>
                                                        {connection.contactName.charAt(0).toUpperCase()} 
                                                    </Avatar>
                                                </ActiveBadge>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={connection.contactName}
                                                secondary={
                                                    <>
                                                        {connection.role === '---' && connection.subTitle === '---' ? (
                                                            <> --- </>
                                                        ) : (
                                                            <>
                                                                <div>{connection.role}</div>
                                                                <div>{connection.subTitle}</div>
                                                            </>)}
                                                    </>
                                                }
                                                primaryTypographyProps={{ fontWeight: 'bold' }}
                                                secondaryTypographyProps={{ color: 'text.secondary' }}
                                            />
                                        </ListBox1>

                                        <ListBox2>
                                            <ListItemText
                                                secondary={`Connected ${renderConnectedSince(connection.connectedSince?.value, connection.connectedSince?.unit)}`}
                                            />
                                        </ListBox2>
                                        <ListBox3>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, borderRadius: '4px', border: '1px solid #0000003B', padding: '6px 12px', cursor: 'pointer', }}>
                                                <TextsmsRoundedIcon sx={{ mr: 1, color: 'rgb(0, 0, 0, 0.54)' }} />
                                                <span>Message</span>
                                            </Box>
                                            <IconButton>
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListBox3>
                                    </ListItem>
                                </ContactBox>
                            )
                        })}
                    </div>
                </InfiniteScroll>
            </ModalBox>
        </Modal>
    )
}

export const ShareProfile = ({ qrCodeUrl, open, onClose, profileType, qrCodeLogo, showQr = true, isDownload = false, handleSuccess, handleFailure, shareData}) => {
    const { callApiFunction } = useApi();
    const [emails, setEmails] = useState([]);
    const [emailInput, setEmailInput] = useState('');
    const [emailError, setEmailError] = useState({
        duplicate: false,
        invalid: false
    });
    const [isDisabled, setIsDisabled] = useState(false);
    const canvasRef = useRef(null);
    const { userDetails } = useUserDetailContext();
    const { openSnackbar } = useQuantityContext();

    let phoneNo = userDetails?.phoneNo || "";
    phoneNo = phoneNo.replace(/\s+/g, "");

    const url = qrCodeUrl;
    const message = url;
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://api.whatsapp.com/send?phone=${phoneNo}&text=${encodedMessage}`;
    const telegramLink = `https://t.me/${phoneNo}?text=${encodedMessage}`;
    const encodedUrl = encodeURIComponent(qrCodeUrl);
    const linkedinPostLink = `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}`;

    // const handleDownload = async () => {
    //     const canvas = document.querySelector('canvas');
    //     const blob = await canvasToImage(canvas, 'image/png'); // Change to 'image/jpeg' for JPEG format
    //     saveAs(blob, 'qrcode.png'); // Change file name and extension accordingly
    // };

    // const canvasToImage = (canvas, type) => {
    //     return new Promise((resolve) => {
    //         canvas.toBlob((blob) => {
    //             resolve(blob);
    //         }, type);
    //     });
    // };

      // Generate platform-specific share links with proper metadata
    const getShareLinks = () => {
        const { title, description, image, url } = shareData || {};
        const shareUrl = url || qrCodeUrl;
        const encodedUrl = encodeURIComponent(shareUrl);
        
        let defaultText = '';
        if (profileType === 'event') {
            const eventDate = shareData?.start_time 
                ? new Date(shareData.start_time).toLocaleDateString() 
                : '';
            const eventTime = shareData?.start_time 
                ? new Date(shareData.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                : '';
            defaultText = `Join this event: ${title}\n\n Date: ${eventDate}\n Time: ${eventTime}\n Location: ${shareData?.location || 'Online'}\n\n${shareUrl}`;
        } else if (profileType === 'job') {
            defaultText = `Job Opportunity: ${title}\n\n Company: ${shareData?.company || ''}\n Location: ${shareData?.location || ''}\n\n${shareUrl}`;
        } else {
            defaultText = `Check this out: ${title || ''}\n\n${description || ''}\n\n${shareUrl}`;
        }

        return {
            whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(defaultText)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(title || 'Check this out')}`,
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(defaultText)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
        };
    };

    const shareLinks = getShareLinks();


    const handleDownload = async () => {
        const canvas = document.querySelector("canvas");

        if (!canvas) {
            console.error("Canvas not found!");
            return;
        }

        const blob = await canvasToImage(canvas, "image/png");
        saveAs(blob, "qrcode.png");
    };

    const canvasToImage = (canvas, type) => {
        return new Promise((resolve, reject) => {
            if (!canvas) {
                reject(new Error("Canvas not found!"));
                return;
            }

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error("Failed to convert canvas to blob!"));
                    return;
                }
                resolve(blob);
            }, type);
        });
    };



    // const handleDownload = async () => {
    //     const svg = document.querySelector(".profile-qr-link-tag svg"); // Get the SVG QR code

    //     if (!svg) {
    //         console.error("SVG not found!");
    //         return;
    //     }

    //     const canvas = document.createElement("canvas");
    //     const ctx = canvas.getContext("2d");

    //     // Convert SVG to a Data URL
    //     const svgData = new XMLSerializer().serializeToString(svg);
    //     const img = new Image();
    //     const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    //     const url = URL.createObjectURL(svgBlob);

    //     img.onload = () => {
    //         canvas.width = img.width;
    //         canvas.height = img.height;
    //         ctx.drawImage(img, 0, 0);

    //         // Convert to PNG and download
    //         canvas.toBlob((blob) => {
    //             saveAs(blob, "qrcode.png");
    //             URL.revokeObjectURL(url);
    //         }, "image/png");
    //     };

    //     img.src = url;
    // };






    const handleAddEmail = (event) => {
        if ((event.key === 'Enter' || event.key === ',' || event.key === ' ') && emailInput && emailInput.trim()) {
            const email = emailInput.trim();

            // Validate email
            if (!emailRegex.test(email) || email.includes(' ')) {
                console.log("INVALID EMAIL!!!");
                setEmailError({ invalid: true, duplicate: false });
                return; // Prevent further execution
            }

            // Check for duplicates
            if (emails.includes(email)) {
                console.log("DUPLICATE EMAIL!!!");
                setEmailError({ duplicate: true, invalid: false });
                return; // Prevent further execution
            }

            // Add the email to the list
            setEmails((prevEmails) => [...prevEmails, email]);
            setEmailInput('');
            setEmailError({ invalid: false, duplicate: false }); // Clear errors
            setIsDisabled(false);
        }
    };

    const handleDeleteEmail = (emailToDelete) => () => {
        setEmails((prevEmails) => prevEmails.filter(email => email !== emailToDelete));
    };

    const handleInputChange = (event, newInputValue) => {
        setEmailInput(newInputValue);
        setEmailError({
            duplicate: false,
            invalid: false
        })
    };

    const handleAutocompleteChange = (event, newValue) => {
        setEmails(newValue);
    };

    const getHelperText = () => {
        let helperText = emailHelperText;
        if (emailError.invalid) {
            helperText = 'No Email-ID or invalid Email-ID';
        } else if (emailError.duplicate) {
            helperText = 'Email-ID already in the list';
        }
        return helperText;
    }

    const handleSend = async (event) => {
        event.preventDefault(); // Prevent default form submission
        if (emails.length === 0) {
            handleFailure('No Email-IDs added');
        } else {
            openSnackbar("Sending E-mails...", "info", 3000);
            setIsDisabled(true)
            try {
                const reqBody = { emails, url, download: isDownload, message: '', name: '', cardType: `${profileType}` }
                const response = await callApiFunction('sendLinkToMultipleEmails', reqBody);
                if (response.success) {
                    setEmails([]);
                    setEmailError({
                        duplicate: false,
                        invalid: false
                    });
                    handleSuccess('Sent Successfully!');
                }
            } catch (error) {
                console.error('Error sending email:', error.message);
                handleFailure('Failed to Send. Try again later.')
            } finally {
                setIsDisabled(false);
            }
        }
    };

  

    

    return (
        <div className='share-qr-container'>
            <Modal
                onClose={onClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                className="share-dialog"
            >
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: "auto", bgcolor: 'background.paper', boxShadow: "0px 4px 4px 0px #00000040", borderRadius: 2, paddingTop: 2, maxHeight: '85%', overflowY: 'auto' }}>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: 'rgb(0, 0, 0, 0.54)',
                        }}
                        disableTouchRipple
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                    <Box
                        dividers
                        sx={{ m: 0, p: 2, width: 500 }}
                    >
                        {showQr ?
                            <>
                                <Box className='pb-3'>
                                    <div className="flex-center">
                                        <QrCodeProfile qrCodeUrl={url} size={175} logo={qrCodeLogo} type="canvas" />
                                    </div>
                                </Box>
                                <Box className="text-center downloadShareButtonNew" gutterBottom>
                                    <button className="download-share-button mt-n1" onClick={handleDownload}>
                                        <Download className="dowload-share-icon" />
                                        Download QR Code
                                    </button>
                                </Box>
                            </> : <> </>
                        }
                        <div>
                            <span>Via email</span>
                            <div className="my-3">
                                <Autocomplete
                                    value={emails}
                                    onChange={handleAutocompleteChange}
                                    inputValue={emailInput}
                                    onInputChange={handleInputChange}
                                    onKeyDown={handleAddEmail}
                                    multiple
                                    freeSolo
                                    options={[]}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                key={index}
                                                variant="outlined"
                                                label={option}
                                                onDelete={() => handleDeleteEmail(option)}
                                                {...getTagProps({ index })}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Email"
                                            placeholder={emails.length < 1 ? "List of E-mail ID's" : ""}
                                            multiline
                                            maxRows={2}
                                            error={emailError.invalid || emailError.duplicate}
                                            helperText={getHelperText()}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="save-bottom" style={{ textAlign: "right" }}>
                            <Button variant='contained'
                                sx={{ backgroundColor: "#0044C6" }}
                                onClick={!isDisabled ? handleSend : undefined}
                                disableTouchRipple>
                                Send
                            </Button>
                        </div>
                        <Box className="mt-3">
                            <div className="share-qr-content">
                                Share with your friends
                            </div>
                            <div className="share-qr-subcontent text-left mt-2">
                                Connect and Share with Friends
                            </div>
                        </Box>
                        <Box>
                            <div className="row mt-3">
                                <div className="d-flex  justify-content-around">
                                    <a href={whatsappLink} target='_blank' rel='noreferrer'>
                                        <img src={whatsapp} alt='whatsapp' className="share-icons mr-3" />
                                    </a>
                                    <a href={linkedinPostLink} target='_blank' rel='noreferrer'>
                                        <img src={linkedin} alt="linkedin" className="share-icons mr-3" />
                                    </a>
                                    <a href={telegramLink} target='_blank' rel='noreferrer'>
                                        <img src={telegram} alt='telegram' className="share-icons" />
                                    </a>
                                </div>
                            </div>
                        </Box>
                        <div className='d-flex align-items-center mt-3 hovering_color_change pointer' onClick={() => copyToClipboard(url, handleSuccess, handleFailure)}>
                            <InsertLinkRoundedIcon />
                            <p className='p-0 m-0 ms-2 group_permissiions_options hover'>Copy Link</p>
                        </div>
                    </Box>
                </Box>
            </Modal>
        </div>
    )
};

export const InviteProfile = ({ open, setModalOpen, handleSuccess, handleFailure }) => {

    const { userDetails } = useUserDetailContext();
    const { openSnackbar } = useQuantityContext();
    const { callApiFunction } = useApi();

    const phoneNo = userDetails?.phoneNo?.replace(/\s+/g, "") || "";
    const inviteLink = 'https://app.neoin.co/welcome'

    const message = `Hey! Check out NEOIN:\nJoin via ${inviteLink}\nDownload our Mobile App: ${androidAppLink}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://api.whatsapp.com/send?phone=${phoneNo}&text=${encodedMessage}`;
    const telegramLink = `https://t.me/${phoneNo}?text=${encodedMessage}`;
    const linkedinPostLink = `https://www.linkedin.com/shareArticle?mini=true&url=${encodedMessage}`;

    const onClose = () => {
        setModalOpen(false);
        setShowEmailTextField(false);
        setShowmessageTextField(false)
    }

    const [showMessageTextField, setShowmessageTextField] = useState(false);
    const [showEmailTextField, setShowEmailTextField] = useState(false);

    const handleEmailInvite = () => {
        setShowEmailTextField(!showEmailTextField)
        if (showMessageTextField) {
            setShowmessageTextField(false);
        }
    }

    const handleSmsInvite = () => {
        setShowmessageTextField(!showMessageTextField)
        if (showEmailTextField) {
            setShowEmailTextField(false);
        }
    }

    const getBackFunction = () => {
        if (!showEmailTextField && !showMessageTextField) {
            onClose();
        } else if (showEmailTextField) {
            handleEmailBack();
        } else if (showMessageTextField) {
            handlePhoneBack();
        }
    }

    const handleEmailBack = () => {
        setShowEmailTextField(false)
        if (showMessageTextField) {
            setShowmessageTextField(false);
        }
    }

    const handlePhoneBack = () => {
        setShowmessageTextField(false)
        if (showMessageTextField) {
            setShowEmailTextField(false);
        }
    }

    const [isDisabled, setIsDisabled] = useState(false);

    // Email Invite states and functions
    const [inputEmail, setInputEmail] = useState(''); // Tracks the input email
    const [emailList, setEmailList] = useState([]); // Tracks the list of added emails
    const [emailErrors, setEmailErrors] = useState({
        duplicate: false,
        empty: false
    })

    // Handle changes in email input
    const handleInputChange = (event) => {
        setInputEmail(event.target.value);
        clearErrors(setEmailErrors);
    };

    // Handle deleting a specific email
    const handleDeleteEmail = (emailToDelete) => {
        setEmailList(emailList.filter((email) => email !== emailToDelete));
    };

    const handleSendEmail = async () => {
        if (emailList?.length === 0) {
            setEmailErrors({ empty: true });
            return;
        }
        setIsDisabled(true);
        try {
            openSnackbar("Sending Invites via E-mail...", "info", 3000);
            const reqBody = { emailList, name: '' }
            // console.log0("IAM from Req body", reqBody)
            const response = await callApiFunction('inviteMultipleEmails', reqBody);
            if (response.success) {
                setEmailList([]);
                clearErrors(setEmailErrors);
                handleSuccess('Invite Sent Successfully!');
            }
        } catch (error) {
            console.error('Error sending email:', error.message);
            handleFailure('Failed to Send. Try again later.')
        } finally {
            setIsDisabled(false);
        }
    }

    // SMS Invite states and functions
    const [inputPhoneNumber, setInputPhoneNumber] = useState(''); // Tracks the input phone number
    const [phoneNumberList, setPhoneNumberList] = useState([]); // Tracks the list of added numbers
    const [phoneErrors, setPhoneErrors] = useState({
        duplicateNumber: false,
        noNumber: false
    })

    const clearErrors = (setErrors) => {
        setErrors({
            duplicate: false,
            empty: false
        })
    }

    const handlePhoneInputChange = (newValue) => {
        setInputPhoneNumber(newValue);
        clearErrors(setPhoneErrors);
    }

    // Handle deleting a specific email
    const handleDeletePhoneNumber = (numberToDelete) => {
        setPhoneNumberList(phoneNumberList?.filter((number) => number !== numberToDelete));
    };

    const handleSendSMSInvites = async () => {
        if (phoneNumberList?.length === 0) {
            setPhoneErrors({ empty: true });
            return;
        }
        setIsDisabled(true);
        try {
            openSnackbar("Sending Invites via Phone Numbers...", "info", 3000);
            const reqBody = { phoneNumberList }
            const response = await callApiFunction('inviteMultipleNumbers', reqBody);
            if (response.success) {
                setPhoneNumberList([]);
                clearErrors(setPhoneErrors);
                handleSuccess('Invite Sent Successfully!');
            }
        } catch (error) {
            console.error('Error sending email:', error.message);
            handleFailure('Failed to Send. Try again later.')
        } finally {
            setIsDisabled(false);
        }
    }

    const handleAddItem = (event, triggeredByIcon, input, list, setList, setInput, setErrors) => {
        try {
            let isInputValid = true;
            // Email validation through standard regex
            if (input === inputEmail) {
                isInputValid = emailRegex.test(input.trim());
            }

            // Phone number validation from mui-tel-input
            if (input === inputPhoneNumber) {
                isInputValid = matchIsValidTel(input);
            }

            // If validation fails
            if (!isInputValid) {
                setErrors({ empty: true });
                return;
            }

            if ((event.key === 'Enter' || triggeredByIcon) && input) {
                // Prevent duplicates
                if (!list.includes(input.trim())) {
                    setErrors({ duplicate: false });
                    setList([...list, input.trim()]);
                    setInput(''); // Clear input after adding
                } else {
                    setErrors({ duplicate: true });
                }
            } else if (!input) {
                setErrors({ empty: true });
            }
        } catch (error) {
            console.log('Error adding to list:', error);
        }
    };

    const handleAddEmail = (event, triggeredByIcon = false) => {
        handleAddItem(event, triggeredByIcon, inputEmail, emailList, setEmailList, setInputEmail, setEmailErrors);
    };

    const handleAddPhoneNumber = (event, triggeredByIcon = false) => {
        handleAddItem(event, triggeredByIcon, inputPhoneNumber, phoneNumberList, setPhoneNumberList, setInputPhoneNumber, setPhoneErrors);
    };

    const handleSendInvites = () => {
        if (showEmailTextField) {
            handleSendEmail();
        } else if (showMessageTextField) {
            handleSendSMSInvites();
        } else {
            return;
        }
    }

    const getHelperText = (errors, label) => {
        if (errors.empty) {
            return `No ${label} or invalid  ${label}`
        } else if (errors.duplicate) {
            return `${label} already in the list`
        } else {
            if (label === 'E-mail ID')
                return emailHelperText;
            else if (label === 'Phone Number')
                return phoneHelperText;
            else
                return " "
        }
    }

    return (
        <Modal open={open} onClose={onClose} >
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: "auto", minWidth: "500px", maxWidth: "550px", bgcolor: 'white', boxShadow: "0px 4px 4px 0px #00000040", borderRadius: 2, padding: '60px 24px 24px', maxHeight: "80%", overflowY: 'auto' }}>
                <div className="modal-heading">
                    <div className='invite-modal-title mb-1'>Invite your Friends and Family</div>
                    <div className='invite-modal-subtitle'>Connect with your friends and family</div>
                </div>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: 16,
                        color: 'rgb(0, 0, 0, 0.60)',
                    }}
                    disableTouchRipple
                >
                    <CloseRoundedIcon />
                </IconButton>

                <Box className="d-flex align-items-center"
                    sx={{
                        position: "absolute",
                        left: 16,
                        top: 16,
                        color: 'rgb(0, 0, 0, 0.60)',
                    }}
                    onClick={getBackFunction} >
                    <IconButton
                        aria-label="close"
                        sx={{
                            marginRight: '4px'
                        }}
                        disableTouchRipple
                    >
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <div className='pointer'>Back</div>
                </Box>

                {(!showEmailTextField && !showMessageTextField) &&
                    <>
                        <div className='flex-center-between mt-3 gap-3'>
                            <div className={`d-flex justify-content-start align-items-center Share_button_Design w-100 pointer ${showEmailTextField ? 'digital-identity' : ''}`} onClick={handleEmailInvite}>
                                <div className='flex-center group_card_ShareMethods icon-20px digital-identity'>
                                    <EmailRoundedIcon style={{ color: "#ffff" }} />
                                </div>
                                <p className="m-0 p-0 group_permissiions_options" style={{ color: showEmailTextField ? 'white' : '#00000099' }}>Invite by Email</p>
                            </div>

                            <div className={`d-flex justify-content-start align-items-center Share_button_Design w-100 pointer ${showMessageTextField ? 'digital-identity' : ''}`} onClick={handleSmsInvite}>
                                <div className='flex-center group_card_ShareMethods icon-20px digital-identity'>
                                    <TextsmsRoundedIcon style={{ color: "#ffff" }} />
                                </div>
                                <p className="m-0 p-0 group_permissiions_options" style={{ color: showMessageTextField ? 'white' : '#00000099' }}>Invite by Message</p>
                            </div>
                        </div>

                        <div className='mb-3' style={{ position: "relative" }}>
                            <hr
                                style={{
                                    marginTop: "30px",
                                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                                }}
                            />
                            <p className="m-0"
                                style={{
                                    position: "absolute",
                                    top: "-18px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    backgroundColor: "#fff",
                                    padding: "5px",
                                }}
                            >
                                Or
                            </p>
                        </div>

                        <div className='flex-center-between'>
                            <a href={whatsappLink} target='_blank' rel='noreferrer'>
                                <img src={whatsapp} alt='whatsapp' className="share-icons mr-3" />
                            </a>
                            <a href={linkedinPostLink} target='_blank' rel='noreferrer'>
                                <img src={linkedin} alt="linkedin" className="share-icons mr-3" />
                            </a>
                            <a href={telegramLink} target='_blank' rel='noreferrer'>
                                <img src={telegram} alt='telegram' className="share-icons" />
                            </a>
                        </div>
                    </>
                }

                {showEmailTextField && (
                    <>
                        <TextField
                            className='my-3'
                            label="E-mail ID"
                            sx={{ width: "100%" }}
                            value={inputEmail}
                            onChange={handleInputChange}
                            onKeyDown={handleAddEmail}
                            error={emailErrors.duplicate || emailErrors.empty}
                            autoFocus
                            InputProps={{
                                endAdornment: (
                                    <>
                                        <InputAdornment position="end">
                                            <IconButton onClick={(event) => handleAddEmail(event, true)} disableTouchRipple>
                                                <Tooltip title='Add to List' arrow disableFocusListener disableTouchListener>
                                                    <AddCircleRoundedIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </InputAdornment>
                                    </>
                                )
                            }}
                            helperText={getHelperText(emailErrors, 'E-mail ID')}
                        />
                        <div className='mb-2'>Receivers' List:</div>
                        <Box className='email-list-container'>
                            {emailList?.map((email, index) => (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => handleDeleteEmail(email)}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </>
                )}

                {showMessageTextField && (
                    <>
                        <MuiTelInput
                            className='my-3'
                            label="Phone Number"
                            fullWidth
                            value={inputPhoneNumber}
                            onChange={handlePhoneInputChange}
                            onKeyDown={handleAddPhoneNumber}
                            error={phoneErrors.duplicate || phoneErrors.empty}
                            MenuProps={PhoneInputMenuProps}
                            defaultCountry='IN'
                            autoFocus
                            InputProps={{
                                endAdornment: (
                                    <>
                                        <InputAdornment position="end">
                                            <IconButton onClick={(event) => handleAddPhoneNumber(event, true)} disableTouchRipple>
                                                <Tooltip title='Add to List' arrow disableFocusListener disableTouchListener>
                                                    <AddCircleRoundedIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </InputAdornment>
                                    </>
                                )
                            }}
                            helperText={getHelperText(phoneErrors, 'Phone Number')}
                        />
                        <div className='mb-2'>Receivers' List :</div>
                        <Box className='email-list-container'>
                            {phoneNumberList?.map((number, index) => (
                                <Chip
                                    key={index}
                                    label={number}
                                    onDelete={() => handleDeletePhoneNumber(number)}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </>
                )}

                <div className='flex-center-between mt-3'>
                    <div className="d-flex align-items-center hovering_color_change pointer" onClick={() => copyToClipboard(inviteLink, handleSuccess, handleFailure)}>
                        <InsertLinkRoundedIcon />
                        <p className='p-0 m-0 ms-2 group_permissiions_options hover'>Copy Invite link</p>
                    </div>
                    <Button variant='contained'
                        sx={{ backgroundColor: "#0044C6" }}
                        onClick={!isDisabled ? handleSendInvites : undefined}
                        disableTouchRipple>
                        Send
                    </Button>
                </div>
                {/* <div className='d-flex gap-3'>
                        <Button onClick={handleContactClose} sx={{ color: "#0044c6" }}>CANCEL</Button>
                        <Button type="submit" variant="contained" sx={{ backgroundColor: "#0044c6" }}>SEND</Button>
                    </div> */}

            </Box>
        </Modal>
    )
}